import { useState, useEffect } from "react";
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiPlusCircle,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import { useCorporateInteriorDesign } from "../../useHook";
import Swal from "sweetalert2";
import { useNavigate, useSearchParams } from "react-router-dom";
import GlobalPagination from "../../../../Components/Pagination/GlobalPagination";
import AddPopularCorporateInteriorDesign from "../../../../Components/Modals/PopularCorporateInteriorDesign/PopularCorporateInteriorDesign";
import SimilarItemsModal from "../../../../Components/Modals/SimilarItems/SimilarItems";
import RecordPosition from "../../../../Components/Modals/RecordPosition/RecordPosition";
import { MdDelete } from "react-icons/md";
import { IoEye } from "react-icons/io5";
import {
  usePermission,
  SystemPermissions,
} from "../../../../Utils/permissionAccess";

const CorporateInteriorDesignList = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(
    SystemPermissions.CorporateInteriorDesign.CREATE,
  );
  const canAddPopular = hasPermission(
    SystemPermissions.CorporateInteriorDesign.ADD_POPULAR_DESIGN,
  );
  const canPosition = hasPermission(
    SystemPermissions.CorporateInteriorDesign.POSITION,
  );
  const canAddSimilar = hasPermission(
    SystemPermissions.CorporateInteriorDesign.ADD_SIMILAR,
  );
  const canView = hasPermission(SystemPermissions.CorporateInteriorDesign.VIEW);
  const canUpdate = hasPermission(
    SystemPermissions.CorporateInteriorDesign.UPDATE,
  );
  const canDelete = hasPermission(
    SystemPermissions.CorporateInteriorDesign.DELETE,
  );
  const canShowActions = canAddSimilar || canUpdate || canView || canDelete;

  const {
    getCorporateInteriorDesign,
    deleteCorporateInteriorDesign,
    updateCorporateInteriorDesignPosition,
  } = useCorporateInteriorDesign();

  const [corporateInteriorDesignData, setCorporateInteriorDesignData] =
    useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const pageParam = parseInt(searchParams.get("page") || "0", 10);
  const offset = pageParam * limit;
  const [showPopularDesignModal, setShowPopularDesignModal] = useState(false);
  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);
  const [selectedDesignForSimilar, setSelectedDesignForSimilar] = useState(null);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedDesignForPosition, setSelectedDesignForPosition] =
    useState(null);

  useEffect(() => {
    loadCorporateInteriorDesigns(offset, limit);
  }, [offset]);

  const handlePageChange = (newOffset) => {
    const newPage = Math.floor(newOffset / limit);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      return next;
    });
  };

  const loadCorporateInteriorDesigns = async (
    currentOffset = offset,
    currentLimit = limit,
    currentSearchFilter = searchFilter,
  ) => {
    setIsLoading(true);
    try {
      await getCorporateInteriorDesign(
        setCorporateInteriorDesignData,
        setTotal,
        currentOffset,
        currentLimit,
        currentSearchFilter,
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load corporate interior designs. Please refresh the page.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "0");
      return next;
    });
    try {
      await getCorporateInteriorDesign(
        setCorporateInteriorDesignData,
        setTotal,
        0,
        limit,
        searchFilter,
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to search corporate interior designs. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchFilter("");
    setSearchParams({ page: "0" });
    loadCorporateInteriorDesigns(0, limit, "");
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });
    if (result.isConfirmed) {
      try {
        await deleteCorporateInteriorDesign(id);
        await loadCorporateInteriorDesigns(offset, limit);
        Swal.fire("Deleted!", "Interior design deleted successfully.", "success");
      } catch (error) {
        console.error("Failed to delete corporate interior design:", error);
        Swal.fire(
          "Error!",
          "Failed to delete corporate interior design. Please try again.",
          "error",
        );
      }
    }
  };

  const handlePositionClick = (design) => {
    setSelectedDesignForPosition(design);
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (corporateInteriorDesignId, newPosition) => {
    await updateCorporateInteriorDesignPosition(
      corporateInteriorDesignId,
      newPosition,
    );
    await loadCorporateInteriorDesigns(offset, limit, searchFilter);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h3 className="text-xl font-bold">Corporate Interior Design</h3>
        <div className="flex flex-wrap gap-2">
          {canCreate && (
            <button
              onClick={() => navigate("/corporate-interior-design/create")}
              className="btn btn-primary h-[44px]"
            >
              <FiPlus className="text-white" />
              Add Corporate Interior Design
            </button>
          )}
          {canAddPopular && (
            <button
              onClick={() => setShowPopularDesignModal(true)}
              className="btn btn-primary h-[44px]"
            >
              <FiPlus className="text-white" />
              Add Popular Design
            </button>
          )}
        </div>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap md:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Title</th>
                <th className="table-th">Room Size</th>
                {canShowActions && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {corporateInteriorDesignData.length > 0 ? (
                corporateInteriorDesignData.map((design, index) => (
                  <tr
                    key={design.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">
                      {canPosition ? (
                        <button
                          type="button"
                          onClick={() => handlePositionClick(design)}
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                        >
                          {design.position ?? offset + index + 1}
                        </button>
                      ) : (
                        <span className="font-medium text-gray-800">
                          {design.position ?? offset + index + 1}
                        </span>
                      )}
                    </td>
                    <td className="table-td">{design.title}</td>
                    <td className="table-td">{design.room_size}</td>
                    {canShowActions && (
                      <td className="table-td">
                        <div className="flex space-x-2">
                          {canAddSimilar && (
                            <button
                              className="btn btn-action"
                              onClick={() => {
                                setSelectedDesignForSimilar(design.id);
                                setIsSimilarModalOpen(true);
                              }}
                            >
                              <FiPlusCircle className="text-white" />
                              Similar Items
                            </button>
                          )}
                          {canUpdate && (
                            <button
                              className="btn btn-edit text-white"
                              onClick={() =>
                                navigate(
                                  `/corporate-interior-design/edit/${design.id}`,
                                )
                              }
                            >
                              <FiEdit2 className="text-white" /> Edit
                            </button>
                          )}
                          {canView && (
                            <button
                              className="btn btn-view"
                              onClick={() =>
                                navigate(`/corporate-interior-design/${design.id}`)
                              }
                            >
                              <IoEye className="text-white" /> View
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn btn-delete text-white"
                              onClick={() => handleDelete(design.id)}
                            >
                              <MdDelete className="text-white" /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={canShowActions ? 4 : 3}
                    className="py-4 px-4 text-center text-gray-500 font-bold"
                  >
                    {isLoading
                      ? "Loading corporate interior designs..."
                      : "No corporate interior designs found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {corporateInteriorDesignData.length > 0 && total > limit && (
        <div className="mt-4">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="bg-white"
          />
        </div>
      )}

      {showPopularDesignModal && (
        <AddPopularCorporateInteriorDesign
          isOpen={showPopularDesignModal}
          onClose={() => setShowPopularDesignModal(false)}
        />
      )}

      {isSimilarModalOpen && (
        <SimilarItemsModal
          isOpen={isSimilarModalOpen}
          onClose={() => {
            setIsSimilarModalOpen(false);
            setSelectedDesignForSimilar(null);
          }}
          sourceId={selectedDesignForSimilar}
          sourceType="corporate_interior_design"
        />
      )}

      {isPositionModalOpen && selectedDesignForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={() => {
            setIsPositionModalOpen(false);
            setSelectedDesignForPosition(null);
          }}
          currentPosition={selectedDesignForPosition.position ?? 0}
          recordId={selectedDesignForPosition.id}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Corporate Interior Design"
        />
      )}
    </div>
  );
};

export default CorporateInteriorDesignList;
